import type { ActivityTime, ActivityView } from '../../app/activity-query'

export type { ActivityView } from '../../app/activity-query'

export type ActivityFrame =
  | 'A01' | 'A02' | 'A03' | 'A04' | 'A05' | 'A06' | 'A07' | 'A08'
  | 'A09' | 'A10' | 'A11' | 'A12' | 'A13' | 'A14' | 'A15' | 'A16' | 'A17'
  | 'M01' | 'M02' | 'M03' | 'M04' | 'M05' | 'M06'
  | 'error'

const FRAMES = new Set<string>([
  'A01', 'A02', 'A03', 'A04', 'A05', 'A06', 'A07', 'A08',
  'A09', 'A10', 'A11', 'A12', 'A13', 'A14', 'A15', 'A16', 'A17',
  'M01', 'M02', 'M03', 'M04', 'M05', 'M06',
  'error',
])

/**
 * `?frame=` 是**设计稿演示态**，只在开发构建里认。
 *
 * 它在 view.tsx 里岔出三十来处分支：写死的未读数、离线/恢复横幅、
 * 只在夹具上成立的分组。原来这个参数没有任何门禁——线上任何人带一个
 * `?frame=A01` 就能把演示态调出来，而那一屏看着跟真的一模一样。
 *
 * `import.meta.env.DEV` 在生产构建里是常量 false，所以这道门是**编译期**的：
 * 生产包里 `search.frame` 恒为 undefined，那三十处分支一处都走不到。
 *
 * 解析与序列化两侧都要拦。只拦一侧的话，另一侧仍然会把它塞回 URL——
 * 而「拦住了一半」在这件事上等于没拦。
 */
function framesEnabled(): boolean {
  return import.meta.env.DEV
}

export type ActivitySearch = {
  view: ActivityView
  project_id?: string
  type?: string
  status?: string
  actor?: string
  q?: string
  time?: ActivityTime
  scope?: 'all' | 'platform'
  activity_id?: string
  frame?: ActivityFrame
  alerts?: boolean
}

export function parseActivitySearch(searchStr: string): ActivitySearch {
  const raw = searchStr.startsWith('?') ? searchStr.slice(1) : searchStr
  const params = new URLSearchParams(raw)
  const view = params.get('view')
  const frame = params.get('frame')
  const scope = params.get('scope')
  const time = params.get('time')
  return {
    view: view === 'all' || view === 'action' || view === 'following' ? view : 'important',
    project_id: params.get('project_id') || undefined,
    type: params.get('type') || undefined,
    status: params.get('status') || undefined,
    actor: params.get('actor') || undefined,
    q: params.get('q') || undefined,
    time: time === 'today' || time === 'yesterday' || time === 'earlier' ? time : undefined,
    scope: scope === 'platform' ? 'platform' : undefined,
    activity_id: params.get('activity_id') || undefined,
    frame: framesEnabled() && frame && FRAMES.has(frame) ? (frame as ActivityFrame) : undefined,
    alerts: params.get('alerts') === '1' || params.get('alerts') === 'true' || undefined,
  }
}

export function activitySearchParams(search: ActivitySearch, lockedProjectId?: string): URLSearchParams {
  const params = new URLSearchParams()
  if (search.view !== 'important') params.set('view', search.view)
  if (!lockedProjectId && search.project_id) params.set('project_id', search.project_id)
  if (search.type) params.set('type', search.type)
  if (search.status) params.set('status', search.status)
  if (search.actor) params.set('actor', search.actor)
  if (search.q) params.set('q', search.q)
  if (search.time) params.set('time', search.time)
  if (search.scope === 'platform') params.set('scope', 'platform')
  if (search.activity_id) params.set('activity_id', search.activity_id)
  if (framesEnabled() && search.frame) params.set('frame', search.frame)
  if (search.alerts) params.set('alerts', '1')
  return params
}

export function activityHref(pathname: string, search: ActivitySearch, lockedProjectId?: string): string {
  const q = activitySearchParams(search, lockedProjectId).toString()
  return q ? `${pathname}?${q}` : pathname
}
